import React, { useEffect, useRef } from 'react';

/**
 * StreamVideo is a wrapper around the HTML5 `<video>` tag.
 * It detects if the video has an HLS (.m3u8) source and:
 * - Plays natively using HLS if supported by the browser (like Safari).
 * - Dynamically imports and initializes `hls.js` to enable HLS streaming in other browsers (like Chrome, Firefox).
 * - Falls back to playing the MP4 source if HLS is unsupported or fails.
 */
const StreamVideo = React.forwardRef(({ videoObj, poster, autoPlay = true, ...props }, ref) => {
  const internalRef = useRef(null);
  const videoRef = ref || internalRef;

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    let hls = null;
    const hlsUrl = videoObj?.hls;
    const mp4Url = videoObj?.mp4;

    let isDestroyed = false;

    const initHls = async () => {
      // 1. If HLS stream URL is provided
      if (hlsUrl) {
        // Dynamically import `hls.js` on the client side (SSR-safe)
        try {
          const { default: Hls } = await import('hls.js');
          if (isDestroyed) return;

          if (Hls.isSupported()) {
            hls = new Hls({
              maxMaxBufferLength: 10, // Limit buffer size to optimize memory/network
              enableWorker: true,
              lowLatencyMode: true,
              autoStartLoad: true,
            });
            // Allow adaptive start level (-1) for fast initial frame display
            hls.startLevel = -1;
            hls.loadSource(hlsUrl);
            hls.attachMedia(video);
            return;
          }
        } catch (e) {
          console.error('Failed to load hls.js dynamically:', e);
        }

        // Test if browser natively supports HLS (Safari/iOS fallback)
        if (video.canPlayType('application/vnd.apple.mpegurl')) {
          video.src = hlsUrl;
          return;
        }
      }

      // 2. Fallback to standard MP4 playback if HLS is not supported/loaded
      if (mp4Url) {
        video.src = mp4Url;
      }
    };

    initHls();

    return () => {
      isDestroyed = true;
      if (hls) {
        hls.destroy();
      }
    };
  }, [videoObj, videoRef]);

  // Resolve poster image from explicit prop or videoObj metadata
  const resolvedPoster = poster || videoObj?.poster || videoObj?.posterUrl || videoObj?.thumbnail;

  // Render video element with poster, autoPlay, and preload metadata
  return (
    <video
      ref={videoRef}
      poster={resolvedPoster}
      autoPlay={autoPlay}
      preload="metadata"
      {...props}
    />
  );
});

StreamVideo.displayName = 'StreamVideo';

export default StreamVideo;
